import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

const SECTION_DOT = '#E8A838';

const countryCodes: { code: string; label: string; flag?: string }[] = [
  { code: '+972', label: 'Israel' },
  { code: '+1', label: 'US/Canada' },
  { code: '+44', label: 'UK' },
  { code: '+33', label: 'France' },
  { code: '+49', label: 'Germany' },
  { code: '+61', label: 'Australia' },
  { code: '+27', label: 'South Africa' },
  { code: '+34', label: 'Spain' },
  { code: '+39', label: 'Italy' },
  { code: '+31', label: 'Netherlands' },
  { code: '+32', label: 'Belgium' },
  { code: '+41', label: 'Switzerland' },
  { code: '+43', label: 'Austria' },
  { code: '+52', label: 'Mexico' },
  { code: '+55', label: 'Brazil' },
  { code: '+54', label: 'Argentina' },
  { code: '+358', label: 'Finland' },
  { code: '+46', label: 'Sweden' },
  { code: '+47', label: 'Norway' },
  { code: '+45', label: 'Denmark' },
];

export type ReadingStyleKey =
  | 'sephardic'
  | 'ashkenazi'
  | 'yamen'
  | 'moroccan'
  | 'jerusalem_sephardic'
  | 'other';

export interface RabbiOnboardingFormValues {
  firstNameHe: string;
  lastNameHe: string;
  firstNameEn: string;
  lastNameEn: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  profileImageFile: File | null;
  readingStyles: ReadingStyleKey[];
  experienceYears: string;
  aboutYourself: string;
  location: string;
  howDidYouHear: string;
}

const initialValues: RabbiOnboardingFormValues = {
  firstNameHe: '',
  lastNameHe: '',
  firstNameEn: '',
  lastNameEn: '',
  email: '',
  phoneCountryCode: '+972',
  phoneNumber: '',
  profileImageFile: null,
  readingStyles: [],
  experienceYears: '',
  aboutYourself: '',
  location: '',
  howDidYouHear: '',
};

const READING_STYLE_KEYS: ReadingStyleKey[] = [
  'sephardic',
  'ashkenazi',
  'yamen',
  'moroccan',
  'jerusalem_sephardic',
  'other',
];

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid #E4E7EB',
  background: '#F9FAFB',
  fontSize: '14px',
  boxSizing: 'border-box',
  color: 'var(--kol-navy, #132A49)',
};

interface RabbiOnboardingFormProps {
  value: RabbiOnboardingFormValues;
  onChange: (v: RabbiOnboardingFormValues) => void;
}

const RabbiOnboardingForm: React.FC<RabbiOnboardingFormProps> = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isRtl = i18n.language?.toLowerCase().startsWith('he') ?? true;
  const tr = (key: string): string => (t(key) ?? '') as string;

  const update = (partial: Partial<RabbiOnboardingFormValues>) => {
    onChange({ ...value, ...partial });
  };

  const toggleReadingStyle = (key: ReadingStyleKey) => {
    const next = value.readingStyles.includes(key)
      ? value.readingStyles.filter((k) => k !== key)
      : [...value.readingStyles, key];
    update({ readingStyles: next });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) update({ profileImageFile: file });
  };

  const sectionLabel = (textKey: string, optional?: boolean) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      {!optional && (
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: SECTION_DOT,
            flexShrink: 0,
          }}
        />
      )}
      <span style={{ fontWeight: 600, fontSize: '14px', color: '#132A49' }}>{t(textKey)}</span>
      {optional && (
        <span
          style={{
            fontSize: '12px',
            color: '#6B7280',
            fontWeight: 400,
          }}
        >
          ({t('optional')})
        </span>
      )}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
        maxWidth: 'min(400px, 90vw)',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      {/* Name in Hebrew */}
      <div>
        {sectionLabel('onboarding_form_name_hebrew')}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
          }}
        >
          <input
            type="text"
            value={value.firstNameHe}
            onChange={(e) => update({ firstNameHe: e.target.value })}
            placeholder={tr('onboarding_form_first_name_he_placeholder')}
            style={inputBaseStyle}
          />
          <input
            type="text"
            value={value.lastNameHe}
            onChange={(e) => update({ lastNameHe: e.target.value })}
            placeholder={tr('onboarding_form_last_name_he_placeholder')}
            style={inputBaseStyle}
          />
        </div>
      </div>

      {/* Name in English (optional) */}
      <div>
        {sectionLabel('onboarding_form_name_english', true)}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
          }}
        >
          <input
            type="text"
            value={value.firstNameEn}
            onChange={(e) => update({ firstNameEn: e.target.value })}
            placeholder={tr('onboarding_form_first_name_en_placeholder')}
            style={inputBaseStyle}
          />
          <input
            type="text"
            value={value.lastNameEn}
            onChange={(e) => update({ lastNameEn: e.target.value })}
            placeholder={tr('onboarding_form_last_name_en_placeholder')}
            style={inputBaseStyle}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        {sectionLabel('onboarding_form_email')}
        <input
          type="email"
          value={value.email}
          onChange={(e) => update({ email: e.target.value })}
          placeholder={tr('onboarding_form_email_placeholder')}
          style={inputBaseStyle}
        />
      </div>

      {/* Phone */}
      <div>
        {sectionLabel('onboarding_form_phone')}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'stretch',
          }}
        >
          <select
            value={value.phoneCountryCode}
            onChange={(e) => update({ phoneCountryCode: e.target.value })}
            style={{
              ...inputBaseStyle,
              width: '100px',
              minWidth: '80px',
            }}
          >
            {countryCodes.map(({ code, label }) => (
              <option key={code} value={code}>
                {code} {label}
              </option>
            ))}
          </select>
          <input
            type="tel"
            value={value.phoneNumber}
            onChange={(e) => update({ phoneNumber: e.target.value })}
            placeholder={tr('onboarding_form_phone_placeholder')}
            style={{ ...inputBaseStyle, flex: 1 }}
          />
        </div>
      </div>

      {/* Profile Picture (optional) */}
      <div>
        {sectionLabel('onboarding_form_profile_picture', true)}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '24px',
            borderRadius: '12px',
            border: '2px dashed #D1D5DB',
            background: '#FAFAFA',
            color: '#6B7280',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>{value.profileImageFile?.name ?? t('onboarding_form_upload_image')}</span>
        </button>
      </div>

      {/* Reading style */}
      <div>
        {sectionLabel('onboarding_form_reading_style')}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {READING_STYLE_KEYS.map((key) => {
            const tKey = `onboarding_form_reading_${key}` as const;
            return (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#132A49',
                }}
              >
                <input
                  type="checkbox"
                  checked={value.readingStyles.includes(key)}
                  onChange={() => toggleReadingStyle(key)}
                  style={{ width: '18px', height: '18px' }}
                />
                {t(tKey)}
              </label>
            );
          })}
        </div>
      </div>

      {/* Years of experience */}
      <div>
        {sectionLabel('onboarding_form_experience_years')}
        <input
          type="text"
          value={value.experienceYears}
          onChange={(e) => update({ experienceYears: e.target.value })}
          placeholder={tr('onboarding_form_experience_placeholder')}
          style={inputBaseStyle}
        />
      </div>

      {/* About yourself */}
      <div>
        {sectionLabel('onboarding_form_about_yourself')}
        <textarea
          value={value.aboutYourself}
          onChange={(e) => update({ aboutYourself: e.target.value })}
          placeholder={tr('onboarding_form_about_placeholder')}
          rows={4}
          style={{
            ...inputBaseStyle,
            resize: 'vertical',
            minHeight: '80px',
          }}
        />
      </div>

      {/* Geographic location */}
      <div>
        {sectionLabel('onboarding_form_location')}
        <input
          type="text"
          value={value.location}
          onChange={(e) => update({ location: e.target.value })}
          placeholder={tr('onboarding_form_location_placeholder')}
          style={inputBaseStyle}
        />
      </div>

      {/* How did you hear about us */}
      <div>
        {sectionLabel('onboarding_form_how_hear')}
        <input
          type="text"
          value={value.howDidYouHear}
          onChange={(e) => update({ howDidYouHear: e.target.value })}
          placeholder={tr('onboarding_form_how_hear_placeholder')}
          style={inputBaseStyle}
        />
      </div>
    </div>
  );
};

export { initialValues };
export default RabbiOnboardingForm;
